<template>
  <div v-cloak data-test-basket-proposal-item class="basket-proposal-item">
    <base-single-checkbox
      v-if="!disableCheckbox"
      :model-value="selected"
      class="basket-proposal-item__checkbox"
      :name="`proposal-checkbox-${proposal.id}`"
      data-test-input="proposal-checkbox"
      data-test-basket-proposal-item-checkbox
      @update:modelValue="onCheckboxClick"
    />
    <span
      data-test-basket-proposal-item-icon
      class="basket-proposal-item__icon icon icon-large"
      :class="iconClass"
    />

    <h3
      v-if="proposal.name"
      data-test-basket-proposal-item-title
      class="basket-proposal-item__title"
      v-html="proposal.name"
    />
    <p
      v-if="proposal.details"
      data-test-basket-proposal-item-details
      class="basket-proposal-item__details"
      v-html="proposal.details"
    />
    <p
      data-test-basket-proposal-item-price
      class="basket-proposal-item__price"
      v-html="formatProposalPrice()"
    />
    <div class="basket-proposal-item__payment-control">
      <VueSelect
        v-if="proposal.paymentInformation.paymentIntervals.length > 0"
        data-test-basket-proposal-item-select-interval
        placeholder="Vælg betaling"
        :name="'PaymentInterval-' + proposal.id"
        :options="proposal.paymentInformation.paymentIntervals"
        label="description"
        :searchable="false"
        :clearable="false"
        :model-value="proposal.paymentInformation.selectedPaymentInterval"
        @update:modelValue="onPaymentIntervalChanged"
      />
    </div>
    <div class="basket-proposal-item__actions">
      <basket-icon-link
        :text="removeProposalLabel"
        class="basket-proposal-item__remove"
        icon-class="icon-trash"
        data-test-basket-proposal-item-remove-proposal
        @click="onRemoveClick"
      />
      <basket-icon-link
        v-if="showDocumentDownload"
        :text="downloadProposalLabel"
        icon-class="icon-download"
        data-test-basket-proposal-item-document-download
        :link="proposal.document.link"
      />
    </div>
    <info-box
      v-if="paymentIntervalInfoBoxText"
      :arrow-align="'right'"
      class="basket-proposal-item__info-box"
      :text="paymentIntervalInfoBoxText"
      data-test-basket-proposal-item-interval-info-box
    />
    <collapsible-panel
      v-if="showCovers"
      class="basket-proposal-item__covers"
      :title="coversLabel"
      data-test-basket-proposal-item-covers
    >
      <proposal-covers
        :covers="proposal.covers"
        :deductible-is-presented="proposal.deductible.isPresented"
        :deductible-label="deductibleLabel"
        :deductible-amount="proposal.deductible.amount"
      />
      <proposal-downloads :downloads="proposal.downloads" />
    </collapsible-panel>
    <collapsible-panel
      v-if="showClauses"
      :title="clausesLabel"
      class="basket-proposal-item__clauses"
      data-test-basket-proposal-item-clauses
    >
      <proposal-clauses :index="index" :clauses="proposal.clauses" />
    </collapsible-panel>
    <info-box
      v-if="proposal.productInformation.length > 0"
      class="basket-proposal-item__product-information-box"
      :text="proposal.productInformation"
      :arrow-show="false"
      data-test-basket-proposal-item-information-box
    />
  </div>
</template>
<script lang="ts">
import CollapsiblePanel from './CollapsiblePanel.vue';
import BasketIconLink from './BasketIconLink.vue';
import ProposalCovers from './ProposalCovers.vue';
import ProposalDownloads from './ProposalDownloads.vue';
import { formatPrice } from './utilities/basketUtilities';
import ProposalClauses from './ProposalClauses.vue';
import BaseSingleCheckbox from '@/components/Forms/BaseSingleCheckbox.vue';
import VueSelect from '@/components/UI/VueSelect.vue';
import InfoBox from '@/components/UI/InfoBox.vue';
import {
  BasketProposal,
  BasketProposalPaymentInterval,
  PaymentIntervalId
} from '@/store/modules/interfaces/ProposalBasketStoreState';
import { defineComponent, PropType } from 'vue';

export default defineComponent({
  name: 'BasketProposalItem',
  components: {
    VueSelect,
    CollapsiblePanel,
    BasketIconLink,
    BaseSingleCheckbox,
    ProposalCovers,
    ProposalDownloads,
    ProposalClauses,
    InfoBox
  },
  emits: ['remove-proposal', 'checkbox-click', 'payment-interval-changed'],
  props: {
    proposal: {
      type: Object as PropType<BasketProposal>,
      default: () => ({
        id: '',
        type: '',
        name: '',
        campaignLine: '',
        price: 0,
        details: '',
        downloads: [],
        externalId: '',
        fees: 0,
        deductible: 0,
        isDigitallyCreated: false,
        selectedPaymentIntervalId: 0,
        productType: 0,
        paymentInformation: {
          selectedPaymentInterval: {
            id: 0,
            description: ''
          },
          defaultPaymentInterval: {
            id: 0,
            description: ''
          },
          paymentIntervals: []
        },
        externalProposalNumber: '',
        covers: [],
        clauses: [],
        document: undefined,
        productInformation: ''
      })
    },
    perYearLabel: {
      type: String,
      default: () => ''
    },
    coversLabel: {
      type: String,
      default: () => ''
    },
    deductibleLabel: {
      type: String,
      default: () => ''
    },
    index: {
      type: Number,
      default: () => undefined
    },
    disableCheckbox: {
      type: Boolean,
      default: () => false
    },
    selected: {
      type: Boolean,
      default: () => true
    },
    clausesLabel: {
      type: String,
      default: () => ''
    },
    halfYearlyPaymentLabel: {
      type: String,
      default: () => ''
    },
    quarterlyPaymentLabel: {
      type: String,
      default: () => ''
    },
    monthlyPaymentLabel: {
      type: String,
      default: () => ''
    },
    removeProposalLabel: {
      type: String,
      default: () => ''
    },
    downloadProposalLabel: {
      type: String,
      default: () => ''
    }
  },
  computed: {
    iconClass(): string {
      return this.proposal.type ? `policy-type-name-${this.proposal.type}` : '';
    },
    showClauses(): boolean {
      return this.proposal.clauses && this.proposal.clauses.length > 0;
    },
    showCovers(): boolean {
      return this.proposal.covers && this.proposal.covers.length > 0;
    },
    showDocumentDownload(): boolean {
      return this.proposal.document !== undefined && this.proposal.document.link !== '';
    },
    paymentIntervalInfoBoxText(): string {
      const defaultPaymentInterval = this.proposal.paymentInformation.paymentIntervals[0];
      const selectedPaymentInterval = this.proposal.paymentInformation.selectedPaymentInterval;
      if (defaultPaymentInterval && defaultPaymentInterval.id === selectedPaymentInterval.id)
        return '';

      switch (this.proposal.paymentInformation.selectedPaymentInterval.id) {
        case PaymentIntervalId.HalfYearly:
          return this.halfYearlyPaymentLabel;
        case PaymentIntervalId.Quarterly:
          return this.quarterlyPaymentLabel;
        case PaymentIntervalId.Monthly:
          return this.monthlyPaymentLabel;
        default:
          return '';
      }
    }
  },
  methods: {
    onRemoveClick(): void {
      this.$emit('remove-proposal', this.proposal);
    },
    onCheckboxClick(checked: boolean): void {
      this.$emit('checkbox-click', this.proposal, checked);
    },
    onPaymentIntervalChanged(value: BasketProposalPaymentInterval): void {
      this.$emit(
        'payment-interval-changed',
        this.proposal.id,
        value,
        this.proposal.externalProposalNumber
      );
    },
    formatProposalPrice(): string {
      return `${formatPrice(this.proposal.price)} ${this.perYearLabel}`;
    }
  }
});
</script>
<style lang="scss" scoped>
@import 'src/styles/all';

.basket-proposal-item {
  padding: rem-calc(30) 0;
  border-bottom: 1px solid $medium-gray;
  display: grid;
  max-width: 100%;
  grid-column-gap: rem-calc(24);

  @include breakpoint(large up) {
    grid-template-columns: min-content min-content minmax(rem-calc(160), 3fr) 2fr;
  }

  @include breakpoint(medium down) {
    grid-template-columns: min-content 1fr min-content;
  }

  &__checkbox {
    @include breakpoint(large up) {
      grid-column-start: 1;
      grid-row: 1 / span 3;

      :deep(label) {
        padding-left: rem-calc(30);
      }
    }

    @include breakpoint(medium down) {
      grid-column: 3 / span 1;
      grid-row: 1 / span 2;
      justify-self: flex-end;

      :deep(label) {
        padding-left: rem-calc(35);
        margin-right: 0;
      }
    }
  }

  &__icon {
    @include breakpoint(large up) {
      grid-column-start: 2;
      grid-row: 1 / span 3;
    }

    @include breakpoint(medium down) {
      grid-column: 1 / span 1;
      grid-row: 1 / span 2;
    }
  }

  &__title {
    @include breakpoint(large up) {
      grid-column-start: 3;
      grid-row-start: 1;
    }

    @include breakpoint(medium down) {
      grid-column: 2 / span 2;
      grid-row: 1 / span 1;
    }

    margin-bottom: 0;
  }

  &__details {
    margin-bottom: 0;
    margin-top: rem-calc(8);

    @include breakpoint(large up) {
      grid-column: 3 / span 1;
      grid-row: 2 / span 1;
    }

    @include breakpoint(medium down) {
      grid-column-start: 2;
      grid-row-start: 2;
    }
  }

  &__price {
    margin-bottom: 0;
    justify-self: flex-end;
    white-space: nowrap;

    @include breakpoint(large up) {
      grid-column-start: 4;
    }

    @include breakpoint(medium down) {
      grid-column: 3 / span 1;
      grid-row: 3 / span 1;
      margin: rem-calc(20) 0;
    }
  }

  &__payment-control {
    @include breakpoint(large up) {
      grid-column: 4 / span 1;
      grid-row: 2 / span 2;
      margin-top: rem-calc(8);
      align-self: flex-end;
    }

    @include breakpoint(medium down) {
      grid-column: 1 / span 3;
      grid-row: 4 / span 1;
    }
  }

  &__actions {
    display: flex;
    margin-top: rem-calc(20);

    @include breakpoint(large up) {
      grid-column: 3 / span 1;
      grid-row: 3 / span 1;
    }

    @include breakpoint(medium down) {
      grid-column: 1 / span 2;
      grid-row: 3 / span 1;
      margin: rem-calc(20) 0;
    }
  }

  &__remove {
    &:after {
      content: '';
      height: 100%;
      width: rem-calc(1);
      border-left: 1px solid $medium-gray;
      margin: 0 rem-calc(20);
    }
  }

  &__info-box {
    @include breakpoint(large) {
      grid-column: 1 / span 4;
      grid-row: 4 / span 1;
      margin-top: rem-calc(20);
      margin-bottom: 0;
    }

    @include breakpoint(medium down) {
      grid-column: 1 / span 3;
      grid-row: 5 / span 1;
      margin-top: rem-calc(8);
      margin-bottom: rem-calc(8);
    }
  }

  &__covers {
    @include breakpoint(large) {
      grid-column: 3 / span 2;
      grid-row: 5 / span 1;
      margin-top: rem-calc(20);
    }

    @include breakpoint(medium down) {
      grid-column: 1 / span 2;
      grid-row: 6 / span 1;
      margin-top: rem-calc(8);
    }
  }

  &__clauses {
    @include breakpoint(large) {
      grid-column: 3 / span 2;
      grid-row: 6 / span 1;
      margin-top: rem-calc(20);
    }

    @include breakpoint(medium down) {
      grid-column: 1 / span 2;
      grid-row: 7 / span 1;
      margin-top: rem-calc(8);
    }
  }

  &__product-information-box {
    @include breakpoint(large) {
      grid-column: 1 / span 4;
      grid-row: 7 / span 1;
      margin-top: rem-calc(20);
      margin-bottom: rem-calc(20);
    }

    @include breakpoint(medium down) {
      grid-column: 1 / span 3;
      grid-row: 8 / span 1;
      margin-top: rem-calc(8);
      margin-bottom: rem-calc(8);
    }
  }
}
</style>
../Forms/BaseSingleCheckbox.vue
